import RepeatIcon from '@mui/icons-material/Repeat'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Decimal from 'decimal.js'
import isEmpty from 'lodash/isEmpty'
import React, { memo, useCallback } from 'react'

import BaseDialog from '@/components/base/BaseDialog'
import Button from '@/components/base/Button'
import CustomNumericFormat from '@/components/base/CustomNumericFormat'
import NumericOutput from '@/components/base/NumericOutput'
import { StyledInput } from '@/components/open-position/styles'
import { OpenPositionViewProps } from '@/components/open-position/types'
import { EVENT_NAMES } from '@/constants/amplitude'
import useAmplitude from '@/hooks/use-amplitude'
import useTrans from '@/hooks/useTrans'

const SetLiquidityDialog = ({
  tokenIn,
  tokenOut,
  bPerAPrice,
  ratios,
  errors,
  isOpenSetLiquidity,
  onApply,
  pricesReverted,
  setPricesReverted,
  values: { minPrice, maxPrice },
  setFieldValue,
  setValues,
}: OpenPositionViewProps) => {
  const trans = useTrans()
  const amplitude = useAmplitude()

  const endAdornment = pricesReverted
    ? `${tokenIn.symbol}/${tokenOut.symbol}`
    : `${tokenOut.symbol}/${tokenIn.symbol}`

  const handleApply = () => {
    if (!isEmpty(errors)) return
    onApply()
  }

  const handleSwitchPrices = useCallback(() => {
    setPricesReverted(!pricesReverted)
    const newMinPrice = new Decimal(maxPrice || 0).pow(-1)
    const newMaxPrice = new Decimal(minPrice || 0).pow(-1)
    const decimals = !pricesReverted ? tokenIn.decimals : tokenOut.decimals
    setValues(
      (values) => ({
        ...values,
        minPrice: newMinPrice.isFinite() ? newMinPrice.toFixed(decimals) : '0',
        maxPrice: newMaxPrice.isFinite() ? newMaxPrice.toFixed(decimals) : '0',
      }),
      true,
    )
    amplitude.track(EVENT_NAMES.liquidity_deposit_revert_price, {
      from_token: tokenIn.symbol,
      to_token: tokenOut.symbol,
    })
  }, [
    amplitude,
    maxPrice,
    minPrice,
    pricesReverted,
    setPricesReverted,
    setValues,
    tokenIn.decimals,
    tokenIn.symbol,
    tokenOut.decimals,
    tokenOut.symbol,
  ])

  return (
    <BaseDialog
      data-testid="set-liquidity-dialog"
      title={trans.liquidity.open_position.set_liquidity_dialog_title}
      body={
        <Stack className="mt-2 w-[30rem] max-w-full space-y-4">
          <Stack
            direction="column"
            justifyContent="space-between"
            alignItems="start"
            className="w-full"
            gap={3}
          >
            <Stack
              gap={1}
              data-testid="box-current-price"
              className="w-full flex-col md:flex-row md:justify-between"
            >
              <Stack>
                <Typography className="text-sm font-normal">
                  {trans.liquidity.open_position.current_price}:
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography className="text-sm font-bold">1</Typography>
                <Typography className="text-sm text-[#B0B2BE]" data-testid="token-a-symbol">
                  {pricesReverted ? tokenOut.symbol : tokenIn.symbol}
                </Typography>
                <Typography className="text-sm text-[#B0B2BE]">=</Typography>
                <Typography className="text-sm font-bold">
                  <NumericOutput
                    decimals={pricesReverted ? tokenIn.decimals : tokenOut.decimals}
                    preserveSignificance
                    value={bPerAPrice?.pow(pricesReverted ? -1 : 1)}
                  />
                </Typography>
                <Typography className="text-sm text-[#B0B2BE]" data-testid="token-b-symbol">
                  {pricesReverted ? tokenIn.symbol : tokenOut.symbol}
                </Typography>
                <Tooltip title={trans.liquidity.open_position.revert_prices_icon}>
                  <RepeatIcon
                    data-testid="icon-swap-token"
                    className="cursor-pointer opacity-70 hover:opacity-100"
                    fontSize="small"
                    onClick={handleSwitchPrices}
                  />
                </Tooltip>
              </Stack>
            </Stack>
            <Stack className="w-full">
              <Typography className="mb-3 text-base font-semibold text-white">
                {trans.liquidity.open_position.min_price}
              </Typography>
              <StyledInput
                inputProps={{
                  'data-testid': 'input-min-price',
                  decimalScale: pricesReverted ? tokenIn.decimals : tokenOut.decimals,
                }}
                inputComponent={CustomNumericFormat as any}
                disableUnderline
                value={minPrice}
                onChange={(event) => setFieldValue('minPrice', event.target.value, true)}
                error={!!errors.minPrice}
                endAdornment={endAdornment}
              />
              {errors.minPrice && (
                <Typography className="text-xs text-[#f44336] pl-3 pt-1">
                  {trans.liquidity.open_position.errors[errors.minPrice as keyof typeof trans.liquidity.open_position.errors]}
                </Typography>
              )}
            </Stack>
            <Stack className="w-full">
              <Typography className="mb-3 text-base font-semibold text-white">
                {trans.liquidity.open_position.max_price}
              </Typography>
              <StyledInput
                inputProps={{
                  'data-testid': 'input-max-price',
                  decimalScale: pricesReverted ? tokenIn.decimals : tokenOut.decimals,
                }}
                inputComponent={CustomNumericFormat as any}
                disableUnderline
                value={maxPrice}
                onChange={(event) => setFieldValue('maxPrice', event.target.value, true)}
                error={!!errors.maxPrice}
                endAdornment={endAdornment}
              />
              {errors.maxPrice && (
                <Typography className="text-xs text-[#f44336] pl-3 pt-1">
                  {trans.liquidity.open_position.errors[errors.maxPrice as keyof typeof trans.liquidity.open_position.errors]}
                </Typography>
              )}
            </Stack>
            {ratios?.[0] && ratios?.[1] && (
              <Stack
                gap={1}
                data-testid="box-deposit-ratio"
                className="w-full flex-col md:flex-row md:justify-between"
              >
                <Stack>
                  <Typography className="text-sm font-normal">
                    {trans.liquidity.open_position.deposit_ratio}:
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography className="text-sm font-bold">{`${ratios[0]
                    .times(100)
                    .toFixed(2)}%`}</Typography>
                  <Typography className="text-sm text-[#B0B2BE]">{tokenIn.symbol}</Typography>
                  <Typography className="text-sm text-[#B0B2BE]">/</Typography>
                  <Typography className="text-sm font-bold">{`${ratios[1]
                    .times(100)
                    .toFixed(2)}%`}</Typography>
                  <Typography className="text-sm text-[#B0B2BE]">{tokenOut.symbol}</Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      }
      isOpened={isOpenSetLiquidity}
      handleClose={() => false}
      showCloseButton={false}
      button={
        <Stack display="flex" flexDirection="row" className="w-full space-x-3">
          <Button
            fullWidth
            size="xl"
            data-testid="apply-change"
            onClick={handleApply}
          >
            {trans.liquidity.open_position.apply}
          </Button>
        </Stack>
      }
    />
  )
}

export default memo(SetLiquidityDialog)
